// packages/schema/src/export-configs.ts
/**
 * Export configs + runs schema — per-Site outbound feed destinations
 * (Facebook Catalog, Google Vehicle Listings, canonical CSV/JSON).
 * See docs/specs/generic-feed-pipeline.md §4.1.
 */
import { sql, desc } from "drizzle-orm";
import {
  boolean,
  index,
  integer,
  jsonb,
  pgEnum,
  pgTable,
  text,
  timestamp,
  uuid,
} from "drizzle-orm/pg-core";
import { sites } from "./tables";

export const exportDestinationEnum = pgEnum("export_destination", [
  "facebook-catalog",
  "google-vehicle-listings",
  "canonical-csv",
  "canonical-json",
]);
export const exportDeliveryEnum = pgEnum("export_delivery", [
  "pull-url",
  "https-post",
  "sftp-push",
]);
export const exportScheduleEnum = pgEnum("export_schedule", [
  "daily-4am",
  "every-hour",
  "manual-only",
]);
export const exportRunStatusEnum = pgEnum("export_run_status", ["running", "success", "failed"]);

/**
 * Shape varies by destination:
 *   facebook-catalog        → { catalogId: string }
 *   google-vehicle-listings → { merchantId: string; countryCode: string }
 *   canonical-csv           → {} (no config required)
 *   canonical-json          → {} (no config required)
 *
 * TODO(Plan 3): narrow to discriminated union when export adapters are built.
 */
export type ExportDestinationConfig = Record<string, unknown>;
export type ExportDeliveryConfig = { ciphertext: string; nonce: string } | null;
export type ExportFilterRules = {
  onlyStatus?: ("available" | "pending" | "sold" | "archived")[];
  minPriceCents?: number;
  maxPriceCents?: number;
  onlyCondition?: ("new" | "used" | "certified-pre-owned")[];
};

export const exportConfigs = pgTable(
  "export_configs",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    siteId: uuid("site_id")
      .notNull()
      .references(() => sites.id, { onDelete: "cascade" }),
    destination: exportDestinationEnum("destination").notNull(),
    destinationConfig: jsonb("destination_config").$type<ExportDestinationConfig>(),
    delivery: exportDeliveryEnum("delivery").notNull(),
    deliveryConfig: jsonb("delivery_config").$type<ExportDeliveryConfig>(),
    filterRules: jsonb("filter_rules").$type<ExportFilterRules>(),
    schedule: exportScheduleEnum("schedule").notNull().default("manual-only"),
    enabled: boolean("enabled").notNull().default(true),
    lastRunAt: timestamp("last_run_at", { withTimezone: true }),
    lastRunStatus: exportRunStatusEnum("last_run_status"),
    createdAt: timestamp("created_at", { withTimezone: true })
      .notNull()
      .default(sql`now()`),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .notNull()
      .default(sql`now()`),
  },
  (t) => ({
    siteIdx: index("export_configs_site_idx").on(t.siteId),
    siteEnabledIdx: index("export_configs_site_enabled_idx").on(t.siteId, t.enabled),
  }),
);

export const exportRuns = pgTable(
  "export_runs",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    siteId: uuid("site_id")
      .notNull()
      .references(() => sites.id, { onDelete: "cascade" }),
    exportConfigId: uuid("export_config_id")
      .notNull()
      .references(() => exportConfigs.id, { onDelete: "cascade" }),
    startedAt: timestamp("started_at", { withTimezone: true })
      .notNull()
      .default(sql`now()`),
    completedAt: timestamp("completed_at", { withTimezone: true }),
    status: exportRunStatusEnum("status").notNull().default("running"),
    vehicleCount: integer("vehicle_count").notNull().default(0),
    payloadBytes: integer("payload_bytes").notNull().default(0),
    error: text("error"),
  },
  (t) => ({
    siteStartedIdx: index("export_runs_site_started_idx").on(t.siteId, desc(t.startedAt)),
    configStartedIdx: index("export_runs_config_started_idx").on(
      t.exportConfigId,
      desc(t.startedAt),
    ),
  }),
);

export type ExportConfig = typeof exportConfigs.$inferSelect;
export type NewExportConfig = typeof exportConfigs.$inferInsert;
export type ExportRun = typeof exportRuns.$inferSelect;
export type NewExportRun = typeof exportRuns.$inferInsert;
